import {
	Card,
	CardContent,
	CardHeader,
	Checkbox,
	Collapse,
	FormControl,
	IconButton,
	InputLabel,
	ListItemText,
	makeStyles,
	MenuItem,
	Select,
	TextField,
	Typography
} from '@material-ui/core'
import { ExpandMoreOutlined } from '@material-ui/icons'
import Autocomplete from '@material-ui/lab/Autocomplete'
import clsx from 'clsx'
import { cloneDeep } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import useConstants from '../../../hooks/useConstants'

import { showError } from '../../../actions/misc/misc.actions'
import debouncedInput from '../../../services/debouncedInput'
import { DeletableChip } from '../../Misc/DeletableChip'

const DebouncedTextField = debouncedInput(TextField, { timeout: 200 })

const useStyles = makeStyles((theme) => ({
	header: {
		padding: '13px 24px',
		userSelect: 'none',
		cursor: 'pointer',
		'& .MuiCardHeader-action': {
			margin: 0
		}
	},
	grid: {
		display: 'grid',
		gridGap: '23px',
		marginBottom: '23px',
		'&:last-child': {
			marginBottom: 0
		}
	},
	gridDouble: {
		gridTemplateColumns: 'repeat(auto-fill, minmax(424px, 1fr))',
		'& .field-wrapper': {
			'& .MuiTextField-root': {
				minWidth: '424px !important'
			}
		}
	},
	gridTriple: {
		gridTemplateColumns: 'repeat(auto-fill, minmax(274px, 1fr))',
		'& .field-wrapper': {
			'& .MuiTextField-root': {
				minWidth: '274px !important'
			},
			'& .MuiFormControl-root': {
				minWidth: '272px !important'
			}
		}
	},
	fullWidth: {
		width: '100%'
	},
	expand: {
		transform: 'rotate(0deg)',
		marginLeft: 'auto',
		transition: theme.transitions.create('transform', {
			duration: theme.transitions.duration.shortest
		}),
		padding: 0
	},
	expandOpen: {
		transform: 'rotate(180deg)'
	},
	chips: {
		display: 'flex',
		flexWrap: 'wrap',
		marginTop: '6px',
		backgroundColor: 'transparent !important'
	},
	footerButton: {
		fontFamily: 'Nunito',
		fontWeight: '800',
		'& .MuiButton-label': {
			lineHeight: '19px',
			fontSize: '14px'
		}
	},
	autoComplete: {
		'& .MuiFilledInput-root': {
			paddingTop: '17px'
		},
		'& .Mui-focused': {
			'& .MuiAutocomplete-endAdornment svg': {
				fill: theme.palette.secondary.main
			}
		},
		'& .MuiAutocomplete-endAdornment svg': {
			fill: '#777679'
		}
	},
	chip: {
		marginRight: 4
	}
}))

const PetFacilityMealsCard = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const { tags } = useConstants()

	let { pet, setPet, facilityPermittedMealAdjustments } = props

	const cadences = ['morning', 'midday', 'evening']
	const bowlAdjustments = [
		'None',
		'Slow Feeder',
		'Tennis Ball',
		'Mini Bowl In Center',
		'Flat-Faced Bowl',
		'Raised Bowl',
		'Metal Bowl',
		'Slow Feeder Metal Bowl'
	]
	const [isExpanded, setIsExpanded] = useState(false)
	const [petMealTags, setPetMealTags] = useState([])

	useEffect(() => {
		if (tags && pet.tags && pet.tags.meal && pet.tags.meal.length) {
			console.log(tags)
			let mealTags = tags.meal.filter((tag) => {
				return pet.tags.meal.findIndex((pt) => pt._id === tag._id) !== -1
			})

			setPetMealTags(mealTags)
		} else {
			setPetMealTags([])
		}
	}, [tags, pet])

	const validateNewFoodSchedule = (updatedCadences) => {
		if (updatedCadences.length === 0) {
			const error = 'Boarding dogs must have at least one meal occurence each day'
			dispatch(showError(error))
			return false
		}
		return true
	}

	const updatePet = (field, value) => {
		setPet({ ...pet, [field]: value })
	}

	const updateMealTags = (value) => {
		let updatedTags = cloneDeep(pet.tags)

		updatedTags['meal'] = value

		updatePet('tags', updatedTags)
	}

	const removeTag = (value) => {
		let updatedTags = cloneDeep(pet.tags)
		let tagIndex = updatedTags['meal'].findIndex((t) => t._id === value._id)

		if (tagIndex !== -1) {
			updatedTags['meal'].splice(tagIndex, 1)
		}

		updatePet('tags', updatedTags)
	}

	const removeCadence = (cadence) => {
		let updatedCadences = [...pet.food_schedule]
		let cadenceIndex = updatedCadences.findIndex((c) => c === cadence)

		if (cadenceIndex !== -1) {
			updatedCadences.splice(cadenceIndex, 1)
		}

		const procceed = validateNewFoodSchedule(updatedCadences)

		if (procceed) updatePet('food_schedule', updatedCadences)
	}

	return (
		<Card>
			<CardHeader
				title="Facility Meals"
				className={classes.header}
				onClick={() => setIsExpanded(!isExpanded)}
				action={
					<IconButton
						className={clsx(classes.expand, {
							[classes.expandOpen]: isExpanded
						})}
						onClick={() => setIsExpanded(!isExpanded)}
						aria-expanded={isExpanded}
						aria-label="show more">
						<ExpandMoreOutlined style={{ fill: '#777679' }} />
					</IconButton>
				}
			/>
			<Collapse in={isExpanded} timeout="auto" unmountOnExit>
				<CardContent>
					<div className={classes.grid}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<Autocomplete
								multiple
								disableCloseOnSelect
								size="small"
								id="meal-cadence"
								className={classes.autoComplete}
								options={cadences || []}
								value={pet.food_schedule && pet.food_schedule.length ? pet.food_schedule : []}
								onChange={(e, v) => {
									const procceed = validateNewFoodSchedule(v)
									if (procceed) updatePet('food_schedule', v)
								}}
								onClose={(e) => {
									e.target.value = ''
								}}
								disabled={!pet.active}
								renderTags={(selected) =>
									selected.map((value) => (
										<DeletableChip
											key={value}
											label={value}
											{...{
												onDelete: pet.active ? () => removeCadence(value) : null
											}}
										/>
									))
								}
								renderOption={(option, { selected }) => (
									<React.Fragment>
										<Checkbox checked={selected} />
										<ListItemText style={{ textTransform: 'capitalize' }} primary={option} />
									</React.Fragment>
								)}
								renderInput={(params) => (
									<TextField {...params} fullWidth variant="filled" label="Meal Times" />
								)}
							/>
						</div>
					</div>
					<div className={classes.grid}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<DebouncedTextField
								id="meal-instructions"
								variant="filled"
								multiline
								label="Meal Instructions"
								fullWidth
								value={pet.facility_food_instructions}
								disabled={!pet.active}
								onChange={(e) => updatePet('facility_food_instructions', e.target.value)}
							/>
						</div>
					</div>
					<div className={classes.grid}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<FormControl className={classes.fullWidth}>
								<InputLabel id="meal-adjustments">Permitted Meal Incentives</InputLabel>
								<Select
									id="meal-adjustments-select"
									variant="filled"
									value={pet.permitted_meal_adjustments || []}
									disabled={false}
									label="Permitted Meal Incentives"
									multiple
									onChange={(e) => {
										updatePet('permitted_meal_adjustments', e.target.value)
									}}
									renderValue={(facs) => (
										<div className={classes.chips}>
											{(pet.permitted_meal_adjustments || []).map((value) => (
												<DeletableChip
													key={value}
													label={value}
													className={classes.chip}
												/>
											))}
										</div>
									)}>
									{facilityPermittedMealAdjustments.map((v) => (
										<MenuItem key={v} value={v}>
											<Checkbox
												checked={(pet.permitted_meal_adjustments || []).indexOf(v) > -1}
											/>
											<ListItemText primary={v} />
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</div>
					</div>
					<div className={classes.grid}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<Typography variant="body2" style={{ fontWeight: 800, marginBottom: 16 }}>
								Meal Tags
							</Typography>
							<Autocomplete
								multiple
								getOptionLabel={(option) => option.name || ''}
								disableCloseOnSelect
								size="small"
								id="meal-tags"
								className={classes.autoComplete}
								options={tags ? tags.meal : []}
								value={petMealTags && petMealTags.length ? petMealTags : []}
								onChange={(e, v) => updateMealTags(v)}
								onClose={(e) => {
									e.target.value = ''
								}}
								disabled={!pet.active}
								renderTags={(selected) =>
									selected.map((value) => (
										<DeletableChip
											key={value._id}
											label={value.name}
											{...{
												onDelete: pet.active ? () => removeTag(value) : null
											}}
										/>
									))
								}
								renderOption={(option, { selected }) => (
									<React.Fragment>
										<Checkbox checked={selected} />
										<ListItemText
											style={{ textTransform: 'capitalize' }}
											primary={option.name}
										/>
									</React.Fragment>
								)}
								renderInput={(params) => (
									<TextField
										{...params}
										variant="filled"
										fullWidth
										label="Select All Meal Tags"
									/>
								)}
							/>
						</div>
					</div>
				</CardContent>
			</Collapse>
		</Card>
	)
}

export default PetFacilityMealsCard
